// src/App.js
//
// Top-level application component
// ===============================
//
// PURPOSE
// -------
// This file defines the global routing structure and the top-level UI shell.
//
// Responsibilities:
// - configure BrowserRouter with a stable basename
// - decide which shell to render:
//     * DashboardLayout -> internal / authenticated application shell
//     * PublicLayout    -> login / register / public shop / legal pages
// - define all route mappings
//
// CURRENT APP ARCHITECTURE
// ------------------------
// Your application is now clearly split into two major route families:
//
// 1) Public routes
//    - login / register
//    - public voucher shop
//    - legal pages
//
// 2) Internal application routes
//    - dashboard
//    - work items
//    - visits / HR / reports
//    - vouchers / admin / settings pages
//    - catalog (services + products operational area)
//
// IMPORTANT DESIGN DECISION
// -------------------------
// We intentionally use a "public-routes-first" shell selection strategy:
//
// - explicitly known public paths -> PublicLayout
// - everything else              -> DashboardLayout
//
// Why this is better than maintaining a giant "dashboard prefixes" list:
// - much lower maintenance overhead
// - new internal pages automatically get the internal shell
// - fewer bugs caused by forgetting to update a prefix list
//
// ROUTER BASEPATH
// ---------------
// The app is intentionally hosted under /app in both DEV and PROD:
//
//   DEV:  http://localhost:3000/app/...
//   PROD: https://hb.konin.pl/app/...
//
// Therefore BrowserRouter basename is hardcoded to "/app".
//
// With basename="/app":
// - browser URL: "/app/dashboard"
// - React Router path seen inside the app: "/dashboard"
//
// IMPORTANT
// ---------
// All route paths in this file MUST be written WITHOUT "/app" prefix.
//
// AUTH MODEL ALIGNMENT
// --------------------
// This file is intentionally aligned with the strict tab-selected auth model.
//
// That means:
//
// - App.js does NOT try to restore auth
// - App.js does NOT decide whether a tab may reuse some other tab's session
// - App.js does NOT inspect cookies or localStorage for auth repair
//
// Instead:
//
// - AuthContext owns bootstrap/auth state
// - ProtectedRoute decides whether a protected page may render
// - LoginPage uses router state.from to return user to the originally requested
//   protected route after explicit login in THIS TAB
//
// This is exactly what we want for flows like:
//
//   user manually enters /app/work-items/123
//   -> internal shell is selected
//   -> ProtectedRoute redirects to login
//   -> after login user returns to /work-items/123
//
// So App.js should stay structurally clean and "auth-neutral" except for
// wrapping protected pages in ProtectedRoute.
// COMMAND SEARCH INTEGRATION
// --------------------------
// Command Search is part of the authenticated application shell, not the public
// shell.
//
// Therefore:
// - CommandSearchProvider is mounted inside DashboardLayout,
// - Navbar is rendered inside CommandSearchProvider,
// - CommandSearchButton can safely call useCommandSearch(),
// - CommandSearchDialog is rendered by CommandSearchProvider itself,
// - PublicLayout remains clean and does not load command search.
//
// IMPORTANT:
// Do NOT render <CommandSearchDialog /> manually in App.js.
// CommandSearchProvider already renders it.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";

import "./App.css";
import "./i18n";

/* -------------------------------------------------------------------------- */
/* Public / auth pages                                                        */
/* -------------------------------------------------------------------------- */

import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

/* -------------------------------------------------------------------------- */
/* Internal / protected pages                                                 */
/* -------------------------------------------------------------------------- */

import Dashboard from "./pages/Dashboard";

/* -------------------------------------------------------------------------- */
/* Master Data                                                                */
/* -------------------------------------------------------------------------- */

import CompanyMasterDataPage from "./pages/MasterData/CompanyMasterDataPage";
import MasterDataPlaceholderPage from "./pages/MasterData/MasterDataPlaceholderPage";
import SalesNumberSeriesPage from "./pages/MasterData/SalesNumberSeriesPage";
import DocumentSettingsPage from "./pages/MasterData/DocumentSettingsPage";
import PaymentMethodsPage from "./pages/MasterData/PaymentMethodsPage";
import LegalTextsPage from "./pages/MasterData/LegalTextsPage";
import WorkItemPage from "./pages/WorkItemPage";
import WorkItemChatPage from "./pages/WorkItemChatPage";
import UploadPage from "./pages/UploadPage";
import ManagePage from "./pages/ManagePage";
import UserManagementPage from "./pages/Admin/UserManagementPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import SettingsPage from "./pages/SettingsPage";
import UserSettingsPage from "./pages/UserSettingsPage";
import AdminPanelPage from "./pages/AdminPanelPage";
import AnnouncementPage from "./pages/AnnouncementPage";
import UserManagementHub from "./pages/Admin/UserManagementHub";
import LeaveTypesPage from "./pages/Admin/LeaveTypesPage";
import LeavesOverviewPage from "./pages/Hr/LeavesOverviewPage";
import AbsenceCalendarPage from "./pages/Hr/AbsenceCalendarPage";
import ApprovalsPage from "./pages/Hr/ApprovalsPage";
import CalendarExceptionsPage from "./pages/Hr/CalendarExceptionsPage";
import WorkPatternsPage from "./pages/Hr/WorkPatternsPage";
import WorkSchedulesPage from "./pages/Hr/WorkSchedulesPage";
import MyHrSettingsPage from "./pages/Hr/MyHrSettingsPage";

/* -------------------------------------------------------------------------- */
/* Calendar                                                                   */
/* -------------------------------------------------------------------------- */

import RBCResourceDay from "./pages/RBCResourceDay";

/* -------------------------------------------------------------------------- */
/* Vouchers                                                                   */
/* -------------------------------------------------------------------------- */

import BasicDataPage from "./pages/Vouchers/BasicDataPage";
import VoucherPage from "./pages/Vouchers/VoucherPage";
import VoucherOverviewPage from "./pages/Vouchers/VoucherOverviewPage";
import RedeemVoucherPage from "./pages/Vouchers/RedeemVoucherPage";
import RedeemEditPage from "./pages/Vouchers/RedeemEditPage";

/* -------------------------------------------------------------------------- */
/* Catalog                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Operational Catalog area.
 *
 * IMPORTANT BUSINESS NOTE
 * -----------------------
 * This is intentionally NOT under /admin.
 *
 * Why:
 * - this module is expected to be used by normal employees,
 * - it is an operational working area, not only a superuser/master-data zone,
 * - backend authorization remains authoritative.
 *
 * Route family:
 * - /catalog/service-categories
 * - /catalog/service-sections
 * - /catalog/services
 * - /catalog/product-categories
 * - /catalog/products
 * - /catalog/publication
 */
import ServiceMainCategoriesPage from "./pages/Catalog/ServiceMainCategoriesPage";
import ServiceSectionsPage from "./pages/Catalog/ServiceSectionsPage";
import ServiceOverviewPage from "./pages/Catalog/ServiceOverviewPage";
import ServicePage from "./pages/Catalog/ServicePage";
import ProductCategoriesPage from "./pages/Catalog/ProductCategoriesPage";
import ProductOverviewPage from "./pages/Catalog/ProductOverviewPage";
import ProductPage from "./pages/Catalog/ProductPage";
import CatalogPublicationPreviewPage from "./pages/Catalog/CatalogPublicationPreviewPage";
import UnitConversionsPage from "./pages/Stock/UnitConversionsPage";

/* -------------------------------------------------------------------------- */
/* Stock / inventory                                                          */
/* -------------------------------------------------------------------------- */

import StockOverviewPage from "./pages/Stock/StockOverviewPage";
import StockControlPage from "./pages/Stock/StockControlPage";
import MaterialExceptionsPage from "./pages/Stock/MaterialExceptionsPage";
import StockLocationsPage from "./pages/Stock/StockLocationsPage";
import UnitsOfMeasurePage from "./pages/Stock/UnitsOfMeasurePage";

/* -------------------------------------------------------------------------- */
/* Sanitary documentation                                                     */
/* -------------------------------------------------------------------------- */

import SanitaryProductsPage from "./pages/Sanitary/SanitaryProductsPage";
import SanitaryRulesPage from "./pages/Sanitary/SanitaryRulesPage";
import SanitaryRuleEditorPage from "./pages/Sanitary/SanitaryRuleEditorPage";
import SanitaryDailySettlementPage from "./pages/Sanitary/SanitaryDailySettlementPage";
import SanitaryEventsPage from "./pages/Sanitary/SanitaryEventsPage";
import SanitarySolutionsPage from "./pages/Sanitary/SanitarySolutionsPage";
import SanitarySolutionEditorPage from "./pages/Sanitary/SanitarySolutionEditorPage";
import SanitaryWorkPatternsPage from "./pages/Sanitary/SanitaryWorkPatternsPage";
import SanitaryReportsPage from "./pages/Sanitary/SanitaryReportsPage";
import SanitaryDailyClosingPage from "./pages/Sanitary/SanitaryDailyClosingPage";

/* -------------------------------------------------------------------------- */
/* Sales / reception sales                                                    */
/* -------------------------------------------------------------------------- */

import SalesOverviewPage from "./pages/Sales/SalesOverviewPage";
import SalesDocumentEditPage from "./pages/Sales/SalesDocumentEditPage";

/* -------------------------------------------------------------------------- */
/* Web Shop                                                                   */
/* -------------------------------------------------------------------------- */

import WebShopOrdersOverviewPage from "./pages/WebShop/WebShopOrdersOverviewPage";

/* -------------------------------------------------------------------------- */
/* Reports                                                                    */
/* -------------------------------------------------------------------------- */

import ReportsPage from "./pages/Reports/ReportsPage";
import IncomeReportPage from "./pages/Reports/IncomeReportPage";
import CommissionReportPage from "./pages/Reports/CommissionReportPage";
import HrAbsencePayrollReportPage from "./pages/Reports/HrAbsencePayrollReportPage";

/* -------------------------------------------------------------------------- */
/* Extended visits-based analytics reports                                    */
/* -------------------------------------------------------------------------- */

import SlotOccupancyReportPage from "./pages/Reports/SlotOccupancyReportPage";
import ServiceProfitabilityReportPage from "./pages/Reports/ServiceProfitabilityReportPage";
import CategoryEconomicsReportPage from "./pages/Reports/CategoryEconomicsReportPage";
import WeekdayEconomicsReportPage from "./pages/Reports/WeekdayEconomicsReportPage";
import IdleWindowsReportPage from "./pages/Reports/IdleWindowsReportPage";
import DailyScheduleQualityReportPage from "./pages/Reports/DailyScheduleQualityReportPage";
import NoShowLossReportPage from "./pages/Reports/NoShowLossReportPage";
import ClientRetentionLiteReportPage from "./pages/Reports/ClientRetentionLiteReportPage";
import PremiumWindowReportPage from "./pages/Reports/PremiumWindowReportPage";
import TrendSeasonalityReportPage from "./pages/Reports/TrendSeasonalityReportPage";

/* -------------------------------------------------------------------------- */
/* Public mini-shop                                                           */
/* -------------------------------------------------------------------------- */

import ShopPage from "./pages/Shop/ShopPage";
import ThankYouPage from "./pages/Shop/ThankYouPage";
import PrivacyPolicyPage from "./pages/Shop/PrivacyPolicyPage";
import VoucherTermsPage from "./pages/Shop/VoucherTermsPage";

/* -------------------------------------------------------------------------- */
/* Layout components                                                          */
/* -------------------------------------------------------------------------- */

import ProtectedRoute from "./components/ProtectedRoute";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import PublicAnnouncementBanner from "./components/announcements/PublicAnnouncementBanner";
import AuthenticatedSocketLifecycle from "./components/realtime/AuthenticatedSocketLifecycle";

/* -------------------------------------------------------------------------- */
/* Global internal-shell providers                                            */
/* -------------------------------------------------------------------------- */
/**
 * CommandSearchProvider owns:
 * - command search open/close state,
 * - Ctrl+K / Cmd+K global shortcut,
 * - "/" shortcut when the user is not typing,
 * - rendering of CommandSearchDialog.
 *
 * It must wrap Navbar because Navbar renders CommandSearchButton, which uses
 * useCommandSearch().
 */
import { CommandSearchProvider } from "./components/commandSearch/CommandSearchProvider";

/* -------------------------------------------------------------------------- */
/* Router basename                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Single source of truth for BrowserRouter basename.
 *
 * We keep it hardcoded because your deployment model is intentionally stable:
 * both development and production use "/app".
 */
const ROUTER_BASENAME = "/app";

/* -------------------------------------------------------------------------- */
/* Public route matching                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Exact public paths.
 *
 * These routes should ALWAYS render PublicLayout.
 */
const PUBLIC_EXACT_PATHS = new Set([
  "/",
  "/register",
  "/shop/thank-you",
  "/polityka-prywatnosci",
  "/regulamin",
]);

/**
 * Public prefixes.
 *
 * This lets entire public sections stay public without listing every child route.
 *
 * Example:
 * - "/shop"
 * - "/shop/..."
 *
 * IMPORTANT:
 * We do NOT include "/" here, because "/" as a prefix would match everything.
 */
const PUBLIC_PREFIXES = ["/shop"];

/**
 * Returns true if a pathname belongs to the public shell.
 *
 * Matching order:
 * 1) exact public paths,
 * 2) public prefixes,
 * 3) everything else is considered internal.
 *
 * DESIGN NOTE
 * -----------
 * This function is only about shell selection.
 * It is NOT an authorization function.
 *
 * Example:
 * - "/dashboard"    -> internal shell,
 * - "/work-items/1" -> internal shell,
 * - "/shop"         -> public shell.
 *
 * Whether the user may actually see the internal page is decided later by
 * ProtectedRoute, not here.
 */
function isPublicPath(pathname) {
  const path = String(pathname || "").trim() || "/";

  if (PUBLIC_EXACT_PATHS.has(path)) {
    return true;
  }

  return PUBLIC_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

/* -------------------------------------------------------------------------- */
/* Internal overview route classification                                      */
/* -------------------------------------------------------------------------- */

/**
 * Routes that use mature overview/table layouts with their own internal
 * scroll area and a bottom PaginationFooter.
 *
 * Why this exists
 * ---------------
 * The legacy Sidebar.css still gives `.main-content` a general 20px padding.
 * That is useful for simple legacy pages, but for professional overview pages
 * it creates a visible artificial strip below the pagination footer.
 *
 * These overview pages already own their own spacing inside the page component
 * itself, for example `.sales-overview { padding: ... 0; }`. Therefore they can
 * safely use a shell variant where the bottom padding of `.main-content` is
 * removed and the page footer can align with the real lower work-area edge.
 *
 * Keep this list explicit. It makes the shell behavior predictable and avoids
 * changing old/simple pages that still rely on the generic main-content padding.
 */
const PAGINATED_OVERVIEW_PATHS = new Set([
  "/manage",
  "/visits/table",
  "/catalog/products",
  "/catalog/services",
  "/sales",
  "/web-shop/orders",
  "/vouchers/overview",
]);

function isPaginatedOverviewPath(pathname) {
  const path = String(pathname || "").trim() || "/";
  return PAGINATED_OVERVIEW_PATHS.has(path);
}

/* -------------------------------------------------------------------------- */
/* Public shell route classification                                           */
/* -------------------------------------------------------------------------- */

/**
 * Return the public shell family for the current public route.
 *
 * Why this exists
 * ---------------
 * The public shell is not one visual product. It contains at least three
 * different layout families:
 *
 * - auth pages   -> Login / Register, centered, image-framed, no app scaling,
 * - shop pages   -> public voucher shop, own viewport and scroll rules,
 * - legal pages  -> public text/legal documents.
 *
 * The internal shell needs compensated height because the authenticated desktop
 * UI uses the historical 90% body scale. Login/Register pages explicitly opted
 * out of that scale for a premium centered image composition. If the internal
 * compensation leaks into LoginPage, the background panel becomes too wide and
 * appears shifted to the right.
 *
 * Keeping this classification in App.js makes the decision central and avoids
 * page-local hacks.
 */
function getPublicShellKind(pathname) {
  const path = String(pathname || "").trim() || "/";

  if (path === "/" || path === "/register") {
    return "auth";
  }

  if (path === "/shop" || path.startsWith("/shop/")) {
    return "shop";
  }

  if (path === "/polityka-prywatnosci" || path === "/regulamin") {
    return "legal";
  }

  return "generic";
}

/* -------------------------------------------------------------------------- */
/* Protected route helper                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Small JSX helper to reduce repetition.
 *
 * Example:
 *   element={withProtection(<Dashboard />)}
 *
 * Optional role-specific protection can be added later:
 *   element={withProtection(<AdminPanelPage />, ["superuser"])}
 *
 * For now, this helper simply wraps the page with ProtectedRoute.
 *
 * IMPORTANT
 * ---------
 * This is the only "auth-aware" concern in App.js:
 * we declare which pages are protected,
 * but the actual auth decision still belongs to ProtectedRoute/AuthContext.
 */
function withProtection(element, requiredRole = undefined) {
  return (
    <ProtectedRoute requiredRole={requiredRole}>
      {element}
    </ProtectedRoute>
  );
}

/* -------------------------------------------------------------------------- */
/* Mobile edge hotzone                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Small helper component for mobile UX.
 *
 * Behavior:
 * - allows opening the sidebar by swiping from the left screen edge,
 * - also exposes a clickable invisible edge hotzone.
 *
 * Why this exists:
 * - on mobile, sidebar discoverability matters,
 * - this improves usability without forcing a visible button here.
 *
 * Notes:
 * - active only below Bootstrap lg breakpoint (< 992px),
 * - all listeners are cleaned up on unmount.
 */
const MobileEdgeHotzone = () => {
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let moved = false;

    const onTouchStart = (e) => {
      const x = e.touches?.[0]?.clientX ?? 0;

      // Start gesture only if touch begins very close to the left edge.
      if (x > 16) return;

      startX = x;
      startY = e.touches?.[0]?.clientY ?? 0;
      moved = false;
    };

    const onTouchMove = (e) => {
      if (!startX) return;

      const x = e.touches?.[0]?.clientX ?? 0;
      const y = e.touches?.[0]?.clientY ?? 0;

      const dx = x - startX;
      const dy = Math.abs(y - startY);

      // Treat as an intentional sidebar swipe:
      // - enough rightward movement,
      // - limited vertical drift.
      if (dx > 30 && dy < 20) {
        moved = true;
      }
    };

    const onTouchEnd = () => {
      if (moved) {
        document.body.classList.add("sidebar-open");
      }

      startX = 0;
      startY = 0;
      moved = false;
    };

    const media = window.matchMedia("(max-width: 991px)");

    const bindHandlers = () => {
      if (media.matches) {
        window.addEventListener("touchstart", onTouchStart, { passive: true });
        window.addEventListener("touchmove", onTouchMove, { passive: true });
        window.addEventListener("touchend", onTouchEnd, { passive: true });
      } else {
        window.removeEventListener("touchstart", onTouchStart);
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("touchend", onTouchEnd);
      }
    };

    bindHandlers();

    // Support both modern and older MediaQueryList APIs.
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", bindHandlers);
    } else if (typeof media.addListener === "function") {
      media.addListener(bindHandlers);
    }

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);

      if (typeof media.removeEventListener === "function") {
        media.removeEventListener("change", bindHandlers);
      } else if (typeof media.removeListener === "function") {
        media.removeListener(bindHandlers);
      }
    };
  }, []);

  return (
    <div
      className="edge-hotzone d-lg-none"
      onClick={() => document.body.classList.add("sidebar-open")}
      aria-hidden="true"
    />
  );
};

/* -------------------------------------------------------------------------- */
/* App root                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Top-level router root.
 */
const App = () => {
  return (
    <Router basename={ROUTER_BASENAME}>
      <AppLayout />
    </Router>
  );
};

/* -------------------------------------------------------------------------- */
/* AppLayout                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Chooses which top-level shell to render for the current route.
 *
 * IMPORTANT:
 * Because basename="/app" is already handled by BrowserRouter,
 * location.pathname here does NOT contain "/app".
 *
 * Example:
 * - browser URL:   "/app/shop"
 * - pathname here: "/shop"
 *
 * AUTH-RELATED NOTE
 * -----------------
 * AppLayout does NOT try to check whether the user is authenticated.
 *
 * It only decides:
 * - public shell,
 * - internal shell.
 *
 * This is important because manually entered protected URLs should still land
 * in the internal shell first, and only then be guarded by ProtectedRoute.
 */
const AppLayout = () => {
  const location = useLocation();

  const publicRoute = isPublicPath(location.pathname);
  const publicShellKind = publicRoute
    ? getPublicShellKind(location.pathname)
    : "internal";

  /**
   * Publish the current shell family on <body>.
   *
   * IMPORTANT REGRESSION FIX
   * ------------------------
   * A previous public-shop centering attempt wrote hard viewport geometry
   * directly into `document.body.style` for public auth/shop pages. That made
   * the public shop easier to isolate, but it introduced an important internal
   * shell risk: inline body width/height/transform values can survive route
   * transitions long enough to override the authenticated shell CSS. The visible
   * symptom is exactly what we want to avoid in the backoffice: a clipped
   * Sidebar and paginated overview footers pushed out of the work area.
   *
   * The correct boundary is:
   * - AppLayout publishes route-aware body classes only.
   * - App.css owns shell geometry for those classes.
   * - LoginPage may keep its own defensive body reset because it is a special
   *   full-screen visual page and already restores its changes locally.
   * - ShopPage remains business-clean; it is still only the voucher-only shop.
   *
   * To be robust after earlier experimental batches, this effect also removes
   * any stale inline shell geometry on every route change. Once those inline
   * overrides are removed, the active body class decides the geometry again:
   *
   *   body.app-shell-internal      -> compensated internal backoffice shell
   *   body.app-shell-public-shop   -> real public 100vw/100dvh shell via CSS
   *   body.app-shell-public-auth   -> real public 100vw/100dvh shell via CSS
   */
  useEffect(() => {
    const body = document.body;

    const managedClasses = [
      "app-shell-internal",
      "app-shell-public",
      "app-shell-public-auth",
      "app-shell-public-shop",
      "app-shell-public-legal",
      "app-shell-public-generic",
    ];

    /**
     * Remove shell-level inline geometry overrides.
     *
     * These properties are intentionally controlled by App.css, not by inline
     * route effects. Keeping them inline is dangerous because inline styles have
     * higher priority than the normal internal shell rules and can leave the
     * Sidebar / overview work area measured against the wrong viewport.
     */
    const clearInlineShellGeometry = () => {
      body.style.removeProperty("transform");
      body.style.removeProperty("transform-origin");
      body.style.removeProperty("width");
      body.style.removeProperty("min-width");
      body.style.removeProperty("max-width");
      body.style.removeProperty("height");
      body.style.removeProperty("min-height");
      body.style.removeProperty("max-height");
      body.style.removeProperty("overflow");

      body.style.removeProperty("--app-ui-scale");
      body.style.removeProperty("--app-ui-scale-inverse");
      body.style.removeProperty("--app-layout-viewport-width");
      body.style.removeProperty("--app-layout-viewport-height");
    };

    body.classList.remove(...managedClasses);
    clearInlineShellGeometry();

    if (publicRoute) {
      body.classList.add("app-shell-public", `app-shell-public-${publicShellKind}`);
    } else {
      body.classList.add("app-shell-internal");
    }

    return () => {
      body.classList.remove(...managedClasses);

      /*
       * Do not restore previously captured inline geometry here. Restoring stale
       * public-shop viewport values is what can break the next internal route.
       * The next AppLayout effect run will add the correct class and App.css will
       * apply the right geometry.
       */
      clearInlineShellGeometry();
    };
  }, [publicRoute, publicShellKind]);

  const appContainerClassName = publicRoute
    ? `app-container app-container--public app-container--public-${publicShellKind}`
    : "app-container app-container--internal";

  return (
    <div className={appContainerClassName}>
      {publicRoute ? <PublicLayout /> : <DashboardLayout />}
    </div>
  );
};


/* -------------------------------------------------------------------------- */
/* Internal shell height synchronization                                      */
/* -------------------------------------------------------------------------- */

/**
 * Keep the internal shell aware of the real rendered Navbar height.
 *
 * Why this exists
 * ---------------
 * The Navbar is no longer guaranteed to be exactly one fixed-height row. Runtime
 * announcements may switch the authenticated Navbar into a more compact or
 * multi-row layout at higher browser zoom levels. If overview pages continue to
 * assume a static 64-70px header, their table area may become too tall and the
 * shared PaginationFooter can be pushed below the visible application work area.
 *
 * The professional shell-level solution is to publish central CSS variables on
 * the internal shell element:
 *
 *   --app-visual-viewport-height  current browser visual viewport height
 *   --app-layout-viewport-height  unscaled layout height after UI-scale compensation
 *   --app-navbar-height           real current Navbar layout height
 *   --app-header-height           backward-compatible alias used by older pages
 *   --app-work-area-height        layout viewport height minus current Navbar height
 *
 * Existing pages that still calculate height from --app-header-height continue
 * to work. Newer overview pages can use --app-work-area-height directly so they
 * align with the remaining shell area instead of guessing the header height.
 *
 * This hook intentionally lives in App.js / DashboardLayout instead of inside
 * every overview page. Pagination visibility is a shell/layout responsibility,
 * not a local table hack.
 */
function readPositiveCssNumber(element, variableName, fallbackValue) {
  if (!element || typeof window === "undefined") {
    return fallbackValue;
  }

  const raw = window
    .getComputedStyle(element)
    .getPropertyValue(variableName)
    .trim();

  const parsed = Number.parseFloat(raw);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallbackValue;
  }

  return parsed;
}

function useInternalShellHeightVariables(shellRef) {
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell || typeof window === "undefined") {
      return undefined;
    }

    const navbar = shell.querySelector(".navbar");

    if (!navbar) {
      return undefined;
    }

    let frameId = null;

    const publish = () => {
      frameId = null;

      const root = document.documentElement;

      /**
       * Global shell scale compensation.
       *
       * App.css intentionally scales the desktop UI through:
       *
       *   body { transform: scale(var(--app-ui-scale)); }
       *
       * getBoundingClientRect() returns the transformed visual height, while CSS
       * height variables must be expressed in the unscaled layout coordinate
       * system. Therefore this hook reads --app-ui-scale and converts the visual
       * viewport height into layout pixels before publishing --app-work-area-height.
       *
       * Without this conversion the shell can look correct at first glance, but
       * bottom-aligned elements such as Sidebar footer or PaginationFooter can
       * remain a few pixels too high after browser zoom / announcement layout
       * changes.
       */
      const appScale = Math.min(
        1,
        Math.max(0.5, readPositiveCssNumber(root, "--app-ui-scale", 1))
      );

      const visualViewportHeight = Math.max(
        0,
        Math.floor(
          window.visualViewport?.height ||
            window.innerHeight ||
            document.documentElement.clientHeight ||
            0
        )
      );

      const layoutViewportHeight = Math.max(
        0,
        Math.floor(visualViewportHeight / appScale)
      );

      /**
       * offsetHeight is used intentionally instead of getBoundingClientRect().height.
       * The latter is affected by the body transform; offsetHeight is the layout
       * height we need for CSS calculations inside the scaled application shell.
       */
      const navbarHeight = Math.max(
        0,
        Math.ceil(navbar.offsetHeight || 70)
      );

      const workAreaHeight = Math.max(0, layoutViewportHeight - navbarHeight);

      shell.style.setProperty(
        "--app-visual-viewport-height",
        `${visualViewportHeight}px`
      );
      shell.style.setProperty(
        "--app-layout-viewport-height",
        `${layoutViewportHeight}px`
      );
      shell.style.setProperty("--app-navbar-height", `${navbarHeight}px`);
      shell.style.setProperty("--app-header-height", `${navbarHeight}px`);
      shell.style.setProperty("--app-work-area-height", `${workAreaHeight}px`);
    };

    const schedule = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(publish);
    };

    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(navbar);
    resizeObserver.observe(shell);

    /**
     * Sidebar collapse is synchronized through body classes. It does not always
     * resize the Navbar itself, but it changes the available app work area and can
     * affect adaptive navbar modes, so we observe body class changes as well.
     */
    const bodyClassObserver = new MutationObserver(schedule);
    bodyClassObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);
    window.addEventListener("orientationchange", schedule);

    schedule();

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      resizeObserver.disconnect();
      bodyClassObserver.disconnect();
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
      window.removeEventListener("orientationchange", schedule);
    };
  }, [shellRef]);
}

/* -------------------------------------------------------------------------- */
/* DashboardLayout                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Internal application shell:
 * - command search provider,
 * - navbar,
 * - sidebar,
 * - main content area.
 *
 * This shell is rendered for all non-public routes.
 *
 * Important architectural note:
 * - shell selection happens here,
 * - actual authorization still happens per route via ProtectedRoute.
 *
 * Command Search note:
 * - CommandSearchProvider must wrap Navbar,
 * - Navbar can then render CommandSearchButton safely,
 * - CommandSearchProvider already renders CommandSearchDialog,
 * - therefore App.js must NOT render CommandSearchDialog separately.
 */
const DashboardLayout = () => {
  const location = useLocation();
  const internalShellRef = useRef(null);

  useInternalShellHeightVariables(internalShellRef);

  const mainContentClassName = isPaginatedOverviewPath(location.pathname)
    ? "main-content main-content--paginated-overview"
    : "main-content";

  /**
   * Close mobile sidebar after every route change.
   * This prevents the sidebar from remaining open after navigation.
   */
  useEffect(() => {
    document.body.classList.remove("sidebar-open");
  }, [location.pathname]);

  /**
   * If viewport becomes desktop-sized, force-remove the mobile sidebar state.
   * This avoids stale mobile CSS classes affecting desktop layout.
   */
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) {
        document.body.classList.remove("sidebar-open");
      }
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <CommandSearchProvider>
      {/*
        Keep the realtime singleton aligned with the authenticated internal
        shell. This component does not render UI and does not poll business data.
        It only performs normal socket activation and reacts to backend runtime
        fingerprint changes observed through already-existing REST responses.
      */}
      <AuthenticatedSocketLifecycle />

      <div className="internal-shell" ref={internalShellRef}>
        <Navbar />

        <div className="dashboard-layout">
          <Sidebar />

          {/* Backdrop shown only on mobile when sidebar is open */}
          <div
            className="mobile-backdrop d-lg-none"
            onClick={() => document.body.classList.remove("sidebar-open")}
            aria-hidden="true"
          />

          {/* Invisible edge area for swipe/click sidebar open on mobile */}
          <MobileEdgeHotzone />

          <div className={mainContentClassName}>
            <Routes>
              {/* ---------------------------------------------------------------- */}
              {/* Core dashboard                                                   */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/dashboard"
                element={withProtection(<Dashboard />)}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Master Data                                                      */}
              {/* ---------------------------------------------------------------- */}
              {/*
                Master Data is introduced as an internal operational foundation
                module, not as part of the existing Admin/User Management
                dictionaries.

                Why this lives directly below Dashboard in the sidebar:
                - Company Master Data will feed invoice printing, voucher/package
                  documents, public shop/legal texts and future integrations,
                - these settings are broader than HR/user dictionaries,
                - backend authorization remains the final security boundary once
                  persistence endpoints are added.

                Phase 1 keeps the pages frontend-only. They define navigation,
                page structure and business vocabulary before backend tables and
                PDF snapshot logic are implemented.
              */}
              <Route
                path="/master-data"
                element={<Navigate to="/master-data/company" replace />}
              />
              <Route
                path="/master-data/company"
                element={withProtection(<CompanyMasterDataPage />)}
              />
              <Route
                path="/master-data/document-settings"
                element={withProtection(<DocumentSettingsPage />)}
              />
              <Route
                path="/master-data/payment-methods"
                element={withProtection(<PaymentMethodsPage />)}
              />
              <Route
                path="/master-data/tax-settings"
                element={withProtection(<MasterDataPlaceholderPage sectionKey="taxSettings" />)}
              />
              <Route
                path="/master-data/sales-number-series"
                element={withProtection(<SalesNumberSeriesPage />)}
              />
              <Route
                path="/master-data/locations-branches"
                element={withProtection(<MasterDataPlaceholderPage sectionKey="locationsBranches" />)}
              />
              <Route
                path="/master-data/legal-texts"
                element={withProtection(<LegalTextsPage />)}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Universal work items                                             */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/work-items/:key"
                element={withProtection(<WorkItemPage />)}
              />
              <Route
                path="/work-items/:key/chat"
                element={withProtection(<WorkItemChatPage />)}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Upload                                                           */}
              {/* ---------------------------------------------------------------- */}
              <Route path="/upload" element={withProtection(<UploadPage />)} />

              {/* ---------------------------------------------------------------- */}
              {/* Visits / calendar                                                */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/manage"
                element={<Navigate to="/visits/table" replace />}
              />
              <Route
                path="/visits/table"
                element={withProtection(<ManagePage />)}
              />
              <Route
                path="/visits/calendar"
                element={withProtection(<RBCResourceDay />)}
              />

              {/* ---------------------------------------------------------------- */}
              {/* HR                                                               */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/hr/leaves"
                element={withProtection(<LeavesOverviewPage />)}
              />

              <Route
                path="/hr/absence-calendar"
                element={withProtection(<AbsenceCalendarPage />)}
              />
              <Route
                path="/hr/approvals"
                element={withProtection(<ApprovalsPage />)}
              />
              <Route
                path="/hr/calendar-exceptions"
                element={withProtection(<CalendarExceptionsPage />)}
              />
              <Route
                path="/hr/my-settings"
                element={withProtection(<MyHrSettingsPage />)}
              />
              <Route
                path="/hr/work-patterns"
                element={withProtection(<WorkPatternsPage />)}
              />
              <Route
                path="/hr/work-schedules"
                element={withProtection(<WorkSchedulesPage />, ["superuser", "reception"])}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Catalog                                                          */}
              {/* ---------------------------------------------------------------- */}
              {/*
                Catalog is an internal operational module for maintaining:
                - service categories,
                - service sections,
                - services,
                - product categories,
                - products,
                - publication preview.

                Important:
                - this route family is intentionally not under /admin,
                - normal authenticated employees are expected to work here,
                - backend authorization still remains the final security boundary.
              */}
              <Route
                path="/catalog/service-categories"
                element={withProtection(<ServiceMainCategoriesPage />)}
              />
              <Route
                path="/catalog/service-sections"
                element={withProtection(<ServiceSectionsPage />)}
              />
              <Route
                path="/catalog/services"
                element={withProtection(<ServiceOverviewPage />)}
              />
              <Route
                path="/catalog/services/new"
                element={withProtection(<ServicePage />)}
              />
              <Route
                path="/catalog/services/:id"
                element={withProtection(<ServicePage />)}
              />
              <Route
                path="/catalog/product-categories"
                element={withProtection(<ProductCategoriesPage />)}
              />
              <Route
                path="/catalog/products"
                element={withProtection(<ProductOverviewPage />)}
              />
              <Route
                path="/catalog/products/new"
                element={withProtection(<ProductPage />)}
              />
              <Route
                path="/catalog/products/:id"
                element={withProtection(<ProductPage />)}
              />
              <Route
                path="/catalog/publication"
                element={withProtection(<CatalogPublicationPreviewPage />)}
              />
              <Route
                path="/catalog/settings/unit-conversions"
                element={<Navigate to="/stock/unit-conversions" replace />}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Stock / inventory                                                */}
              {/* ---------------------------------------------------------------- */}
              <Route path="/stock" element={withProtection(<StockOverviewPage />)} />
              <Route path="/stock/controls" element={withProtection(<StockControlPage />, ["superuser", "reception", "user"])} />
              <Route
                path="/stock/locations"
                element={withProtection(<StockLocationsPage />, ["superuser"])}
              />
              <Route
                path="/stock/units-of-measure"
                element={withProtection(<UnitsOfMeasurePage />, ["superuser"])}
              />
              <Route
                path="/stock/unit-conversions"
                element={withProtection(<UnitConversionsPage />, ["superuser"])}
              />
              <Route
                path="/stock/material-exceptions"
                element={withProtection(<MaterialExceptionsPage />)}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Sanitary documentation                                           */}
              {/* ---------------------------------------------------------------- */}
              <Route path="/sanitary" element={<Navigate to="/sanitary/products" replace />} />
              <Route
                path="/sanitary/products"
                element={withProtection(<SanitaryProductsPage />)}
              />
              <Route
                path="/sanitary/rules"
                element={withProtection(<SanitaryRulesPage />)}
              />
              <Route
                path="/sanitary/rules/new"
                element={withProtection(<SanitaryRuleEditorPage />)}
              />
              <Route
                path="/sanitary/rules/:ruleId"
                element={withProtection(<SanitaryRuleEditorPage />)}
              />
              <Route
                path="/sanitary/daily-settlement"
                element={withProtection(<SanitaryDailySettlementPage />)}
              />
              <Route
                path="/sanitary/events"
                element={withProtection(<SanitaryEventsPage />)}
              />
              <Route
                path="/sanitary/solutions"
                element={withProtection(<SanitarySolutionsPage />)}
              />
              <Route
                path="/sanitary/solutions/definitions/new"
                element={withProtection(<SanitarySolutionEditorPage />)}
              />
              <Route
                path="/sanitary/solutions/definitions/:definitionId"
                element={withProtection(<SanitarySolutionEditorPage />)}
              />
              <Route
                path="/sanitary/work-patterns"
                element={withProtection(<SanitaryWorkPatternsPage />)}
              />
              <Route
                path="/sanitary/reports"
                element={withProtection(<SanitaryReportsPage />)}
              />
              <Route
                path="/sanitary/daily-closing"
                element={withProtection(<SanitaryDailyClosingPage />, ["superuser"])}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Sales / reception sales                                          */}
              {/* ---------------------------------------------------------------- */}
              <Route path="/sales" element={withProtection(<SalesOverviewPage />)} />
              <Route path="/sales/orders" element={<Navigate to="/web-shop/orders" replace />} />
              <Route path="/sales/new" element={withProtection(<SalesDocumentEditPage />)} />
              <Route path="/sales/:id" element={withProtection(<SalesDocumentEditPage />)} />

              {/* ---------------------------------------------------------------- */}
              {/* Web Shop                                                         */}
              {/* ---------------------------------------------------------------- */}
              <Route path="/web-shop/orders" element={withProtection(<WebShopOrdersOverviewPage />)} />

              {/* ---------------------------------------------------------------- */}
              {/* Reports                                                          */}
              {/* ---------------------------------------------------------------- */}
              <Route path="/reports" element={withProtection(<ReportsPage />)} />
              <Route
                path="/reports/income"
                element={withProtection(<IncomeReportPage />, ["superuser"])}
              />
              <Route
                path="/reports/commission"
                element={withProtection(<CommissionReportPage />)}
              />

              <Route
                path="/reports/hr/absence-payroll"
                element={withProtection(<HrAbsencePayrollReportPage />, ["superuser"])}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Visits-based analytics reports                                   */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/reports/slot-performance"
                element={withProtection(<SlotOccupancyReportPage />)}
              />
              <Route
                path="/reports/service-profitability"
                element={withProtection(<ServiceProfitabilityReportPage />)}
              />
              <Route
                path="/reports/category-economics"
                element={withProtection(<CategoryEconomicsReportPage />)}
              />
              <Route
                path="/reports/weekday-economics"
                element={withProtection(<WeekdayEconomicsReportPage />)}
              />
              <Route
                path="/reports/idle-windows"
                element={withProtection(<IdleWindowsReportPage />)}
              />
              <Route
                path="/reports/daily-schedule-quality"
                element={withProtection(<DailyScheduleQualityReportPage />)}
              />
              <Route
                path="/reports/no-show-loss"
                element={withProtection(<NoShowLossReportPage />)}
              />
              <Route
                path="/reports/client-retention-lite"
                element={withProtection(<ClientRetentionLiteReportPage />)}
              />
              <Route
                path="/reports/premium-windows"
                element={withProtection(<PremiumWindowReportPage />)}
              />
              <Route
                path="/reports/trend-seasonality"
                element={withProtection(<TrendSeasonalityReportPage />)}
              />
              <Route
                path="/reports/slot-occupancy"
                element={withProtection(<SlotOccupancyReportPage />)}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Settings / profile                                               */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/settings"
                element={withProtection(<SettingsPage />, ["superuser"])}
              />
              <Route
                path="/change-password"
                element={withProtection(<ChangePasswordPage />, ["superuser"])}
              />
              <Route
                path="/user-settings/:userId"
                element={withProtection(<UserSettingsPage />, ["superuser"])}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Admin / management                                               */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/admin-panel"
                element={withProtection(<AdminPanelPage />, ["superuser"])}
              />
              <Route
                path="/admin/users"
                element={withProtection(<UserManagementHub />, ["superuser"])}
              />
              <Route
                path="/admin/master-data/leave-types"
                element={withProtection(<LeaveTypesPage />, ["superuser"])}
              />
              <Route
                path="/users"
                element={withProtection(<UserManagementPage />, ["superuser"])}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Announcements                                                    */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/announcement"
                element={withProtection(<AnnouncementPage />)}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Vouchers                                                         */}
              {/* ---------------------------------------------------------------- */}
              <Route
                path="/vouchers/basic-data"
                element={withProtection(<BasicDataPage />)}
              />
              <Route
                path="/vouchers/voucher"
                element={withProtection(<VoucherPage />)}
              />
              <Route
                path="/vouchers/:id"
                element={withProtection(<VoucherPage />)}
              />
              <Route
                path="/vouchers/overview"
                element={withProtection(<VoucherOverviewPage />)}
              />
              <Route
                path="/vouchers/redeem"
                element={withProtection(<RedeemVoucherPage />)}
              />
              <Route
                path="/vouchers/redeems/:redeemId/edit"
                element={withProtection(<RedeemEditPage />)}
              />

              {/* ---------------------------------------------------------------- */}
              {/* Internal fallback                                                */}
              {/* ---------------------------------------------------------------- */}
              {/*
                Any unknown non-public route lands here.

                We redirect to "/dashboard" rather than directly to login because:
                - the route is already in the internal shell family,
                - /dashboard itself is protected,
                - ProtectedRoute on /dashboard will still decide whether login
                  is required.

                This is also useful for strict tab-selected auth:
                App.js should not make its own auth decision here.
              */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </CommandSearchProvider>
  );
};

/* -------------------------------------------------------------------------- */
/* PublicLayout                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Public shell:
 * - login,
 * - register,
 * - public voucher shop,
 * - legal pages.
 *
 * These routes intentionally render without the internal navbar/sidebar shell
 * and without CommandSearchProvider.
 */
const PublicLayout = () => {
  const location = useLocation();

  const [announcementLayout, setAnnouncementLayout] = useState({
    visible: false,
    height: 0,
  });

  const handleAnnouncementLayoutChange = useCallback((nextLayout) => {
    const nextVisible = Boolean(nextLayout?.visible);
    const nextHeight = nextVisible ? Math.max(0, Math.ceil(Number(nextLayout?.height || 0))) : 0;

    setAnnouncementLayout((prev) => {
      if (prev.visible === nextVisible && prev.height === nextHeight) {
        return prev;
      }

      return {
        visible: nextVisible,
        height: nextHeight,
      };
    });
  }, []);

  const publicShellKind = getPublicShellKind(location.pathname);

  const publicLayoutClassName = [
    "public-layout",
    `public-layout--${publicShellKind}`,
    announcementLayout.visible ? "public-layout--has-announcement" : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={publicLayoutClassName}
      style={{
        "--public-announcement-height": `${announcementLayout.height}px`,
      }}
    >
      {/*
        Public runtime announcements are rendered at shell level, not inside
        ShopPage. This keeps the voucher-only shop flow independent from generic
        operational communication banners.

        The banner reports its rendered height so PublicLayout can expose it as
        a CSS variable when route-specific layout corrections are needed. The
        public shell itself is flex-based: the banner takes its natural height,
        and the routed content receives the remaining viewport height. This is
        important because body/#root scrolling is globally disabled in this app,
        so ShopPage must keep its own `.shop-page-viewport` scroll container.
      */}
      <PublicAnnouncementBanner
        currentPath={location.pathname}
        onLayoutChange={handleAnnouncementLayoutChange}
      />

      <div className="public-layout__content">
        <Routes>
        {/* -------------------------------------------------------------------- */}
        {/* Auth                                                                 */}
        {/* -------------------------------------------------------------------- */}
        <Route path="/" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* -------------------------------------------------------------------- */}
        {/* Public shop                                                          */}
        {/* -------------------------------------------------------------------- */}
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/thank-you" element={<ThankYouPage />} />

        {/* -------------------------------------------------------------------- */}
        {/* Legal pages                                                          */}
        {/* -------------------------------------------------------------------- */}
        <Route path="/polityka-prywatnosci" element={<PrivacyPolicyPage />} />
        <Route path="/regulamin" element={<VoucherTermsPage />} />

        {/* -------------------------------------------------------------------- */}
        {/* Public fallback                                                      */}
        {/* -------------------------------------------------------------------- */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default App;
